import React, { useEffect, useMemo, useState } from 'react';
import { Check, Copy, KeyRound, RotateCw, Trash2, X } from 'lucide-react';
import { getGoogleMapReviewsMcpUrl, mcpTokenService } from '../../services/mcpTokenService';

const DEFAULT_MCP_APP_ID = 'mcp_google_map_reviews';
const DEFAULT_SERVER_NAME = 'mcp_google_map_reviews';

const formatDate = (value) => {
  if (!value) return 'None';
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
};

const latestActiveToken = (tokens) => (
  tokens.find((token) => token.status === 'active') || null
);

const MCPTokenPanel = ({
  isOpen,
  onClose,
  appId = DEFAULT_MCP_APP_ID,
  serverName = DEFAULT_SERVER_NAME,
  title = 'Google Map Reviews MCP',
  mcpUrl = getGoogleMapReviewsMcpUrl(),
  defaultRole = 'exporter'
}) => {
  const [tokens, setTokens] = useState([]);
  const [plainToken, setPlainToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const activeToken = useMemo(() => latestActiveToken(tokens), [tokens]);
  const configText = useMemo(() => {
    const tokenValue = plainToken || '<mcp_token>';
    return JSON.stringify({
      mcpServers: {
        [serverName]: {
          url: mcpUrl,
          headers: {
            Authorization: `Bearer ${tokenValue}`
          }
        }
      }
    }, null, 2);
  }, [mcpUrl, plainToken, serverName]);

  const loadTokens = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await mcpTokenService.listTokens(appId);
      setTokens(response.tokens || []);
    } catch (loadError) {
      setError(loadError.message || 'Failed to load MCP tokens.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTokens();
    } else {
      setError('');
      setCopied('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appId]);

  const copyText = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch (_error) {
      setError('Copy failed. Select the text manually.');
    }
  };

  const handleCreate = async () => {
    setIsCreating(true);
    setError('');
    setCopied('');
    try {
      const response = await mcpTokenService.createToken({ app: appId, role: defaultRole });
      setPlainToken(response.token || '');
      await loadTokens();
    } catch (createError) {
      setError(createError.message || 'Failed to generate MCP token.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!activeToken) return;
    setIsRevoking(true);
    setError('');
    try {
      await mcpTokenService.revokeToken(activeToken.token_id);
      setPlainToken('');
      await loadTokens();
    } catch (revokeError) {
      setError(revokeError.message || 'Failed to revoke MCP token.');
    } finally {
      setIsRevoking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mcp-token-panel-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="mcp-token-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mcp-token-panel-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="mcp-token-panel-header">
          <div className="mcp-token-panel-title-row">
            <KeyRound size={18} aria-hidden="true" />
            <div>
              <h2 id="mcp-token-panel-title">{title}</h2>
              <p>Personal token for Codex, Claude, and Manus.</p>
            </div>
          </div>
          <button className="mcp-icon-button" onClick={onClose} aria-label="Close MCP token panel">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {error && <div className="mcp-token-error">{error}</div>}

        <div className="mcp-token-status">
          <div>
            <span className={`mcp-token-status-dot ${activeToken ? 'active' : ''}`} />
            <span className="mcp-token-status-label">{activeToken ? 'Active token' : 'No active token'}</span>
          </div>
          {activeToken && (
            <span className="mcp-token-meta">Expires {formatDate(activeToken.expires_at)}</span>
          )}
        </div>

        {plainToken && (
          <div className="mcp-token-secret">
            <div className="mcp-token-secret-header">
              <span>New token</span>
              <button className="mcp-inline-button" onClick={() => copyText(plainToken, 'token')}>
                {copied === 'token' ? <Check size={14} /> : <Copy size={14} />}
                {copied === 'token' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <code>{plainToken}</code>
          </div>
        )}

        <div className="mcp-config-block">
          <div className="mcp-config-header">
            <span>MCP client config</span>
            <button className="mcp-inline-button" onClick={() => copyText(configText, 'config')}>
              {copied === 'config' ? <Check size={14} /> : <Copy size={14} />}
              {copied === 'config' ? 'Copied' : 'Copy'}
            </button>
          </div>
          <pre>{configText}</pre>
        </div>

        <div className="mcp-token-actions">
          <button
            className="mcp-primary-button"
            onClick={handleCreate}
            disabled={isCreating || isLoading}
          >
            <RotateCw size={15} aria-hidden="true" />
            {activeToken ? 'Regenerate token' : 'Generate token'}
          </button>
          <button
            className="mcp-secondary-button"
            onClick={handleRevoke}
            disabled={!activeToken || isRevoking || isLoading}
          >
            <Trash2 size={15} aria-hidden="true" />
            Revoke
          </button>
        </div>
      </div>
    </div>
  );
};

export default MCPTokenPanel;
